import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, FieldErrors } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'
import NProgress from 'nprogress'

import { Modal } from 'components/Modal'
import { Button, ButtonTextColorVariantEnum, ButtonVariantEnum } from 'components/Button'
import { Form, getFormErrorsArrayFromObject, useForm, ValidationSchema } from 'components/Form/Form'
import { Icon } from 'components/Icon'
import { FileField } from 'components/FileField'
import { useToaster } from 'hooks/useToaster'
import { ApkSupportIssueReq, SupportIssueCategory, SupportIssueCategoryValue } from 'api/models'
import { useApi } from 'contexts/di-context'
import { showGotItToaster } from 'components/ToastGotIt'
import { Select } from 'components/Select'
import { Input } from 'components/Input'
import { Textarea } from 'components/Textarea'

const categoryOptions: SupportIssueCategoryValue[] = Object.values(SupportIssueCategory)

interface Props {
  isVisible: boolean
  onClose: () => void
}

type SupportFormProps = Omit<ApkSupportIssueReq, 'category' | 'apkFile' | 'executionLog'> & {
  category: SupportIssueCategoryValue | null
  apkFile: File | null
  executionLog: string | null
}

export const CustomerSupportModal = ({ isVisible, onClose }: Props) => {
  const toaster = useToaster()
  const { projectUrlName } = useParams()
  const [isEmptyApkAllowed, setIsEmptyApkAllowed] = useState(false)
  const formContext = useForm<SupportFormProps>({
    validationSchema: customerSupportFormSchema,
    formProps: {
      defaultValues: {
        category: null,
        subject: '',
        description: '',
        executionLog: null,
        apkFile: null,
      },
    },
  })
  const category = formContext.watch('category')

  const { t } = useTranslation()
  const api = useApi()
  const [isInProgress, setIsInProgress] = useState(false)
  const shouldReset = useRef(false)

  const handleSubmitSuccess = useCallback(
    async (formProps: SupportFormProps) => {
      if (
        shouldShowApkSection(formProps.category) &&
        formProps.apkFile == null &&
        formProps.executionLog == null &&
        !isEmptyApkAllowed
      ) {
        toaster.info('customerSupport.errors.apkRequired')
        setIsEmptyApkAllowed(true)
        return
      }
      if (formProps.category == null) {
        throw new Error('It looks like the category required check is missing.')
      }
      try {
        const reqData: ApkSupportIssueReq = {
          ...formProps,
          category: formProps.category,
          apkFile: formProps.apkFile ?? undefined,
          executionLog: formProps.executionLog ?? undefined,
        }
        if (projectUrlName != null) {
          reqData.projectName = projectUrlName
        }
        NProgress.start()
        setIsInProgress(true)
        const res = await api.postSupportIssue(reqData)
        console.info(`The support ticket id: ${res.id} has been created`)
        shouldReset.current = true
        onClose()
        showGotItToaster(t('customerSupport.submitted'), 'customerSupport.submitted')
      } catch (err) {
        console.error(err)
        toaster.error(err, t('customerSupport.errors.submitFailed'))
      } finally {
        NProgress.done()
        setIsInProgress(false)
        setIsEmptyApkAllowed(false)
      }
    },
    [isEmptyApkAllowed, toaster, projectUrlName, api, onClose, t],
  )
  const handleSubmitInvalid = useCallback(
    (errors: FieldErrors<ApkSupportIssueReq>) => {
      const errorMessages = getFormErrorsArrayFromObject(errors)
      errorMessages.forEach((errorMsg) => {
        toaster.error(errorMsg)
      })
    },
    [toaster],
  )

  const handleActionButtonPress = useMemo(() => {
    if (isInProgress) {
      return
    }
    return formContext.handleSubmit(handleSubmitSuccess, handleSubmitInvalid)
  }, [isInProgress, formContext, handleSubmitSuccess, handleSubmitInvalid])

  const onModalClose = useCallback(() => {
    if (isInProgress) {
      return
    }
    onClose()
  }, [onClose, isInProgress])

  const afterClose = useCallback(() => {
    if (shouldReset.current) {
      formContext.reset()
      shouldReset.current = false
    }
  }, [shouldReset, formContext])

  return (
    <Modal
      hiddenCloseButton={true}
      title={t('customerSupport.title')}
      isOpen={isVisible}
      onClose={onModalClose}
      afterClose={afterClose}
      actionButton={{
        children: t('submit'),
        textColorVariant: isInProgress
          ? ButtonTextColorVariantEnum.Muted
          : ButtonTextColorVariantEnum.Primary,
        onClick: handleActionButtonPress,
      }}
      preventKeyEvents
    >
      <Form
        formContext={formContext}
        onSubmit={handleSubmitSuccess}
        onSubmitInvalid={handleSubmitInvalid}
      >
        <div>
          <p className="whitespace-pre-line text-small mt-[25px] mb-[45px]">
            {t('customerSupport.description')}
          </p>
        </div>
        <SubTitle>{t('customerSupport.helpCategory')}</SubTitle>
        <Controller
          name="category"
          render={({ field: { onChange, value } }) => (
            <Select
              className="mb-[25px]"
              buttonClassName="bg-gray-inputBG"
              options={categoryOptions.map((categoryOption) => ({
                value: categoryOption,
                label: t(`customerSupport.categoryOptions.${categoryOption}`),
              }))}
              placeholder={t('customerSupport.helpCategoryPlaceholder')}
              disabled={isInProgress}
              onChange={onChange}
              value={value}
            />
          )}
        />
        <div className="mb-[25px]">
          <SubTitle>{t('customerSupport.subject')}</SubTitle>
          <Input
            {...formContext.register('subject')}
            showCounter={false}
            maxLength={35}
            className="bg-gray-inputBG rounded-sm"
            placeholder={t('customerSupport.subjectPlaceholder')}
            disabled={isInProgress}
          />
        </div>
        <SubTitle>{t('customerSupport.howHelp')}</SubTitle>
        <Textarea
          {...formContext.register('description')}
          maxLength={1000}
          className="bg-gray-inputBG min-h-[104px] mb-[33px] rounded-sm"
          placeholder={t('customerSupport.howHelpPlaceholder')}
          disabled={isInProgress}
        />
        {shouldShowApkSection(category) && (
          <>
            <SubTitle>{t('customerSupport.uploadApk')}</SubTitle>
            <FileField<ApkSupportIssueReq, 'apkFile'>
              fieldName="apkFile"
              className="mb-[33px]"
              disabled={isInProgress}
            />
          </>
        )}
        {shouldShowCodeSection(category) && (
          <>
            <SubTitle>{t('customerSupport.executionLog')}</SubTitle>
            <Textarea
              {...formContext.register('executionLog')}
              className="bg-gray-inputBG min-h-[104px] rounded-sm"
              placeholder={t('customerSupport.executionLogPlaceholder')}
              disabled={isInProgress}
            />
          </>
        )}
        <Button
          type="button"
          variant={ButtonVariantEnum.Text}
          className="absolute p-[5px] top-[14px] right-[14px] text-white group"
          onClick={onClose}
          disabled={isInProgress}
        >
          <Icon icon="cross-icon" className="text-gray-normal group-hover:text-white" />
        </Button>
      </Form>
    </Modal>
  )
}

function SubTitle({ children }: { children: React.ReactNode }) {
  return <h3 className="text-normal tracking-wide font-medium mb-[11px]">{children}</h3>
}

const categoriesToShowApkSections: SupportIssueCategoryValue[] = [
  SupportIssueCategory.RUN_APP,
  SupportIssueCategory.RECORD_TRACE,
  SupportIssueCategory.RECORD_VIDEO,
  SupportIssueCategory.MISSING_FRAME_COUNTER,
  SupportIssueCategory.UPLOAD_TRACE,
  SupportIssueCategory.UPLOAD_VIDEO,
  SupportIssueCategory.TRACE_QUALITY,
]

const shouldShowApkSection = (category: SupportIssueCategoryValue | null) =>
  Boolean(category && categoriesToShowApkSections.includes(category))

const shouldShowCodeSection = (category: SupportIssueCategoryValue | null) =>
  Boolean(category === SupportIssueCategory.RUN_APP)

export const customerSupportFormSchema = yup.object<ValidationSchema<ApkSupportIssueReq>>({
  category: yup.string().required('customerSupport.errors.categoryRequired'),
  subject: yup.string().required('customerSupport.errors.subjectRequired'),
  description: yup
    .string()
    .required('customerSupport.errors.descriptionRequired')
    .min(10, 'customerSupport.errors.descriptionMin10'),
})
